import { jsPDF } from 'jspdf';
import { Tenant, Sale, SaleItem, ServiceOrder, Patient, MedicalRecord, VaccinationRecord, DewormingRecord, Budget, BudgetItem } from '@/types';

// Colores de la marca
const COLORS = {
  primary: [107, 91, 79] as [number, number, number],      // taupe-600
  secondary: [139, 119, 101] as [number, number, number],  // taupe-500
  text: [55, 46, 39] as [number, number, number],          // taupe-800
  lightText: [107, 91, 79] as [number, number, number],    // taupe-600
  border: [214, 203, 189] as [number, number, number],     // cream-300
  background: [250, 247, 243] as [number, number, number], // cream-50
};

// Formatear moneda
const formatCurrency = (value: number | string | undefined): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value || 0;
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(num);
};

// Formatear fecha
const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Formatear fecha y hora
const formatDateTime = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Calcular edad
const calculateAge = (birthDate?: string): string => {
  if (!birthDate) return '-';
  const birth = new Date(birthDate);
  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    years--;
  }
  if (years < 1) {
    const months = (today.getFullYear() - birth.getFullYear()) * 12 + today.getMonth() - birth.getMonth();
    return `${months} meses`;
  }
  return `${years} años`;
};

// Agregar cabecera con datos del tenant (con logo opcional)
const addHeader = async (doc: jsPDF, tenant: Tenant, title: string): Promise<number> => {
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 15;
  const logoSize = 25;
  let hasLogo = false;

  // Intentar cargar el logo si existe
  if (tenant.logo_url) {
    try {
      const logoUrl = `${window.location.origin}${tenant.logo_url}`;
      const img = await loadImage(logoUrl);
      if (img) {
        doc.addImage(img, 'PNG', 15, y - 5, logoSize, logoSize);
        hasLogo = true;
      }
    } catch (e) {
      // Si falla, continuar sin logo
      console.warn('No se pudo cargar el logo:', e);
    }
  }

  // Nombre de la veterinaria (ajustar posición si hay logo)
  const textStartX = hasLogo ? 15 + logoSize + 5 : pageWidth / 2;
  const textAlign = hasLogo ? 'left' : 'center';

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text(tenant.name, textStartX, y, { align: textAlign as any });
  y += 6;

  // Datos de contacto
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.lightText);

  const contactLines: string[] = [];
  if (tenant.address) contactLines.push(tenant.address);
  if (tenant.phone) contactLines.push(`Tel: ${tenant.phone}`);

  contactLines.forEach((line) => {
    doc.text(line, textStartX, y, { align: textAlign as any });
    y += 4;
  });

  // Asegurar que y esté después del logo si hay uno
  if (hasLogo) {
    y = Math.max(y, 15 - 5 + logoSize + 5);
  }

  y += 3;

  // Línea separadora
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(15, y, pageWidth - 15, y);
  y += 8;

  // Título del documento
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.text);
  doc.text(title, pageWidth / 2, y, { align: 'center' });
  y += 10;

  return y;
};

// Helper para cargar imagen
const loadImage = (url: string): Promise<string | null> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } else {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

// Agregar pie de página
const addFooter = (doc: jsPDF, pageNumber: number, totalPages: number): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.lightText);

  // Fecha de generación
  doc.text(
    `Generado: ${formatDateTime(new Date().toISOString())}`,
    15,
    pageHeight - 10
  );

  // Número de página
  doc.text(
    `Página ${pageNumber} de ${totalPages}`,
    pageWidth - 15,
    pageHeight - 10,
    { align: 'right' }
  );
};

// ============================================================
// COMPROBANTE DE VENTA
// ============================================================
export const generateSaleReceipt = async (sale: Sale, tenant: Tenant): Promise<void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  let y = await addHeader(doc, tenant, 'COMPROBANTE DE VENTA');

  // Información de la venta
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.text);

  // Recuadro con info
  doc.setFillColor(...COLORS.background);
  doc.roundedRect(15, y, pageWidth - 30, 28, 3, 3, 'F');
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.text('N° Comprobante:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(sale.receipt_number || `#${sale.id}`, 60, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Fecha:', pageWidth / 2 + 10, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDateTime(sale.created_at), pageWidth / 2 + 30, y);
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.text('Cliente:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(sale.owner_name || 'Consumidor Final', 60, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Atendido por:', pageWidth / 2 + 10, y);
  doc.setFont('helvetica', 'normal');
  doc.text(sale.user_name || '-', pageWidth / 2 + 45, y);
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.text('Forma de pago:', 20, y);
  doc.setFont('helvetica', 'normal');
  const paymentLabels: Record<string, string> = {
    cash: 'Efectivo',
    card: 'Tarjeta',
    transfer: 'Transferencia',
    mixed: 'Mixto',
  };
  doc.text(paymentLabels[sale.payment_method] || sale.payment_method, 60, y);
  y += 15;

  // Tabla de items
  doc.setFillColor(...COLORS.primary);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Producto/Servicio', 20, y + 5.5);
  doc.text('Cant.', pageWidth - 85, y + 5.5, { align: 'center' });
  doc.text('P. Unit.', pageWidth - 60, y + 5.5, { align: 'right' });
  doc.text('Subtotal', pageWidth - 20, y + 5.5, { align: 'right' });
  y += 12;

  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'normal');

  sale.items.forEach((item: SaleItem, index: number) => {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }

    if (index % 2 === 0) {
      doc.setFillColor(...COLORS.background);
      doc.rect(15, y - 4, pageWidth - 30, 8, 'F');
    }

    const productName = item.product_name || `Producto #${item.product_id}`;
    doc.text(productName.substring(0, 40), 20, y);
    doc.text(item.quantity.toString(), pageWidth - 85, y, { align: 'center' });
    doc.text(formatCurrency(item.unit_price), pageWidth - 60, y, { align: 'right' });
    doc.text(formatCurrency(item.subtotal), pageWidth - 20, y, { align: 'right' });

    if (item.discount_percentage > 0) {
      y += 5;
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.lightText);
      doc.text(`  Descuento: ${item.discount_percentage}%`, 20, y);
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.text);
    }

    y += 8;
  });

  // Línea separadora
  y += 2;
  doc.setDrawColor(...COLORS.border);
  doc.line(pageWidth - 80, y, pageWidth - 15, y);
  y += 8;

  // Totales
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', pageWidth - 60, y, { align: 'right' });
  doc.text(formatCurrency(sale.subtotal), pageWidth - 20, y, { align: 'right' });
  y += 6;

  if (sale.discount_amount > 0) {
    doc.setTextColor(220, 38, 38); // red-600
    doc.text(`Descuento (${sale.discount_percentage}%):`, pageWidth - 60, y, { align: 'right' });
    doc.text(`-${formatCurrency(sale.discount_amount)}`, pageWidth - 20, y, { align: 'right' });
    doc.setTextColor(...COLORS.text);
    y += 6;
  }

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', pageWidth - 60, y, { align: 'right' });
  doc.text(formatCurrency(sale.total), pageWidth - 20, y, { align: 'right' });
  y += 15;

  // Mensaje de agradecimiento
  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.lightText);
  doc.text('¡Gracias por su confianza!', pageWidth / 2, y, { align: 'center' });

  addFooter(doc, 1, 1);

  // Descargar
  doc.save(`comprobante-${sale.receipt_number || sale.id}.pdf`);
};

// ============================================================
// COMPROBANTE DE COMANDA
// ============================================================
export const generateServiceOrderReceipt = async (order: ServiceOrder, tenant: Tenant): Promise<void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  let y = await addHeader(doc, tenant, 'DETALLE DE COMANDA');

  // Información de la comanda
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.text);

  // Recuadro con info
  doc.setFillColor(...COLORS.background);
  doc.roundedRect(15, y, pageWidth - 30, 28, 3, 3, 'F');
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.text('Comanda N°:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(`#${order.id}`, 55, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Fecha:', pageWidth / 2 + 10, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDateTime(order.created_at), pageWidth / 2 + 30, y);
  y += 7;

  if (order.patient_name) {
    doc.setFont('helvetica', 'bold');
    doc.text('Paciente:', 20, y);
    doc.setFont('helvetica', 'normal');
    doc.text(order.patient_name, 55, y);
  }

  if (order.owner_name) {
    doc.setFont('helvetica', 'bold');
    doc.text('Propietario:', pageWidth / 2 + 10, y);
    doc.setFont('helvetica', 'normal');
    doc.text(order.owner_name, pageWidth / 2 + 45, y);
  }
  y += 7;

  const statusLabels: Record<string, string> = {
    open: 'Abierta',
    ready_to_charge: 'Lista para cobrar',
    charged: 'Cobrada',
    cancelled: 'Cancelada',
  };
  doc.setFont('helvetica', 'bold');
  doc.text('Estado:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(statusLabels[order.status] || order.status, 55, y);

  if (order.created_by_name) {
    doc.setFont('helvetica', 'bold');
    doc.text('Creada por:', pageWidth / 2 + 10, y);
    doc.setFont('helvetica', 'normal');
    doc.text(order.created_by_name, pageWidth / 2 + 45, y);
  }
  y += 15;

  // Tabla de items
  doc.setFillColor(...COLORS.primary);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Producto/Servicio', 20, y + 5.5);
  doc.text('Cant.', pageWidth - 85, y + 5.5, { align: 'center' });
  doc.text('P. Unit.', pageWidth - 60, y + 5.5, { align: 'right' });
  doc.text('Subtotal', pageWidth - 20, y + 5.5, { align: 'right' });
  y += 12;

  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'normal');

  order.items.forEach((item, index) => {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }

    if (index % 2 === 0) {
      doc.setFillColor(...COLORS.background);
      doc.rect(15, y - 4, pageWidth - 30, 8, 'F');
    }

    const productName = item.product_name || `Producto #${item.product_id}`;
    doc.text(productName.substring(0, 40), 20, y);
    doc.text(item.quantity.toString(), pageWidth - 85, y, { align: 'center' });
    doc.text(formatCurrency(item.unit_price), pageWidth - 60, y, { align: 'right' });
    doc.text(formatCurrency(item.subtotal), pageWidth - 20, y, { align: 'right' });

    if (item.notes) {
      y += 5;
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.lightText);
      doc.text(`  Nota: ${item.notes.substring(0, 50)}`, 20, y);
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.text);
    }

    y += 8;
  });

  // Línea separadora
  y += 2;
  doc.setDrawColor(...COLORS.border);
  doc.line(pageWidth - 80, y, pageWidth - 15, y);
  y += 8;

  // Total
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', pageWidth - 60, y, { align: 'right' });
  doc.text(formatCurrency(order.total), pageWidth - 20, y, { align: 'right' });

  addFooter(doc, 1, 1);

  // Descargar
  doc.save(`comanda-${order.id}.pdf`);
};

// ============================================================
// HISTORIA CLÍNICA COMPLETA
// ============================================================
interface MedicalHistoryData {
  patient: Patient;
  medicalRecords: MedicalRecord[];
  vaccinations: VaccinationRecord[];
  dewormings: DewormingRecord[];
}

export const generateMedicalHistory = async (data: MedicalHistoryData, tenant: Tenant): Promise<void> => {
  const { patient } = data;
  // Ordenar cronologicamente (mas viejo primero) para la historia clinica
  const medicalRecords = [...data.medicalRecords].reverse();
  const vaccinations = [...data.vaccinations].reverse();
  const dewormings = [...data.dewormings].reverse();
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let pageNumber = 1;

  let y = await addHeader(doc, tenant, 'HISTORIA CLÍNICA');

  // ========== DATOS DEL PACIENTE ==========
  doc.setFillColor(...COLORS.background);
  doc.roundedRect(15, y, pageWidth - 30, 45, 3, 3, 'F');
  y += 7;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('DATOS DEL PACIENTE', 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setTextColor(...COLORS.text);

  // Fila 1
  doc.setFont('helvetica', 'bold');
  doc.text('Nombre:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(patient.name, 45, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Especie:', 85, y);
  doc.setFont('helvetica', 'normal');
  doc.text(patient.species, 110, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Raza:', 145, y);
  doc.setFont('helvetica', 'normal');
  doc.text(patient.breed || '-', 165, y);
  y += 7;

  // Fila 2
  const sexLabels: Record<string, string> = { male: 'Macho', female: 'Hembra' };
  doc.setFont('helvetica', 'bold');
  doc.text('Sexo:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(sexLabels[patient.sex] || '-', 45, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Edad:', 85, y);
  doc.setFont('helvetica', 'normal');
  doc.text(calculateAge(patient.birth_date), 110, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Peso:', 145, y);
  doc.setFont('helvetica', 'normal');
  doc.text(patient.weight ? `${patient.weight} kg` : '-', 165, y);
  y += 7;

  // Fila 3
  doc.setFont('helvetica', 'bold');
  doc.text('Color:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(patient.color || '-', 45, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Microchip:', 85, y);
  doc.setFont('helvetica', 'normal');
  doc.text(patient.microchip_number || '-', 120, y);
  y += 7;

  // Propietario
  if (patient.owner) {
    doc.setFont('helvetica', 'bold');
    doc.text('Propietario:', 20, y);
    doc.setFont('helvetica', 'normal');
    doc.text(`${patient.owner.first_name} ${patient.owner.last_name}`, 55, y);
    if (patient.owner.phone) {
      doc.text(`- Tel: ${patient.owner.phone}`, 120, y);
    }
  }
  y += 7;

  // Alergias
  if (patient.allergies) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38); // red
    doc.text('Alergias:', 20, y);
    doc.setFont('helvetica', 'normal');
    doc.text(patient.allergies, 50, y);
    doc.setTextColor(...COLORS.text);
  }
  y += 15;

  // Función para verificar si hay espacio
  const checkNewPage = (needed: number): void => {
    if (y + needed > 270) {
      addFooter(doc, pageNumber, 0); // Se actualizará al final
      doc.addPage();
      pageNumber++;
      y = 20;
    }
  };

  // ========== VACUNACIONES ==========
  if (vaccinations.length > 0) {
    checkNewPage(40);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text('REGISTRO DE VACUNACIONES', 20, y);
    y += 8;

    doc.setFillColor(...COLORS.primary);
    doc.rect(15, y, pageWidth - 30, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text('Fecha', 20, y + 5);
    doc.text('Vacuna', 50, y + 5);
    doc.text('Dosis', 120, y + 5);
    doc.text('Lote', 145, y + 5);
    doc.text('Próxima', 175, y + 5);
    y += 10;

    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'normal');

    vaccinations.forEach((vax, index) => {
      checkNewPage(8);
      if (index % 2 === 0) {
        doc.setFillColor(...COLORS.background);
        doc.rect(15, y - 4, pageWidth - 30, 7, 'F');
      }
      doc.text(formatDate(vax.application_date), 20, y);
      doc.text((vax as any).product_name?.substring(0, 35) || '-', 50, y);
      doc.text(`#${vax.dose_number}`, 120, y);
      doc.text(vax.batch_number || '-', 145, y);
      doc.text(formatDate(vax.next_dose_date), 175, y);
      y += 7;
    });
    y += 10;
  }

  // ========== DESPARASITACIONES ==========
  if (dewormings.length > 0) {
    checkNewPage(40);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text('REGISTRO DE DESPARASITACIONES', 20, y);
    y += 8;

    doc.setFillColor(...COLORS.primary);
    doc.rect(15, y, pageWidth - 30, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text('Fecha', 20, y + 5);
    doc.text('Tipo', 50, y + 5);
    doc.text('Producto', 85, y + 5);
    doc.text('Dosis', 145, y + 5);
    doc.text('Próxima', 175, y + 5);
    y += 10;

    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'normal');

    const dewormingTypeLabels: Record<string, string> = {
      internal: 'Interna',
      external: 'Externa',
      both: 'Ambas',
    };

    dewormings.forEach((dew, index) => {
      checkNewPage(8);
      if (index % 2 === 0) {
        doc.setFillColor(...COLORS.background);
        doc.rect(15, y - 4, pageWidth - 30, 7, 'F');
      }
      doc.text(formatDate(dew.application_date), 20, y);
      doc.text(dewormingTypeLabels[dew.deworming_type] || dew.deworming_type, 50, y);
      doc.text(dew.product_name?.substring(0, 30) || '-', 85, y);
      doc.text(dew.dose || '-', 145, y);
      doc.text(formatDate(dew.next_dose_date), 175, y);
      y += 7;
    });
    y += 10;
  }

  // ========== CONSULTAS MÉDICAS ==========
  if (medicalRecords.length > 0) {
    checkNewPage(20);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text('HISTORIAL DE CONSULTAS', 20, y);
    y += 10;

    medicalRecords.forEach((record, index) => {
      checkNewPage(60);

      // Encabezado de consulta
      doc.setFillColor(...COLORS.secondary);
      doc.rect(15, y, pageWidth - 30, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`Consulta ${index + 1} - ${formatDate(record.visit_date)}`, 20, y + 5.5);
      if (record.weight) {
        doc.text(`Peso: ${record.weight} kg`, pageWidth - 50, y + 5.5);
      }
      y += 12;

      doc.setTextColor(...COLORS.text);
      doc.setFontSize(9);

      if (record.reason) {
        doc.setFont('helvetica', 'bold');
        doc.text('Motivo:', 20, y);
        doc.setFont('helvetica', 'normal');
        const reasonLines = doc.splitTextToSize(record.reason, pageWidth - 60);
        doc.text(reasonLines, 50, y);
        y += reasonLines.length * 5 + 3;
      }

      if (record.symptoms) {
        checkNewPage(15);
        doc.setFont('helvetica', 'bold');
        doc.text('Examen clínico:', 20, y);
        doc.setFont('helvetica', 'normal');
        const symptomLines = doc.splitTextToSize(record.symptoms, pageWidth - 60);
        doc.text(symptomLines, 50, y);
        y += symptomLines.length * 5 + 3;
      }

      if (record.diagnosis) {
        checkNewPage(15);
        doc.setFont('helvetica', 'bold');
        doc.text('Diagnóstico:', 20, y);
        doc.setFont('helvetica', 'normal');
        const diagLines = doc.splitTextToSize(record.diagnosis, pageWidth - 60);
        doc.text(diagLines, 55, y);
        y += diagLines.length * 5 + 3;
      }

      if (record.treatment) {
        checkNewPage(15);
        doc.setFont('helvetica', 'bold');
        doc.text('Tratamiento:', 20, y);
        doc.setFont('helvetica', 'normal');
        const treatLines = doc.splitTextToSize(record.treatment, pageWidth - 60);
        doc.text(treatLines, 55, y);
        y += treatLines.length * 5 + 3;
      }

      if (record.prescription) {
        checkNewPage(15);
        doc.setFont('helvetica', 'bold');
        doc.text('Receta:', 20, y);
        doc.setFont('helvetica', 'normal');
        const prescLines = doc.splitTextToSize(record.prescription, pageWidth - 50);
        doc.text(prescLines, 45, y);
        y += prescLines.length * 5 + 3;
      }

      if (record.next_visit_date) {
        checkNewPage(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.primary);
        doc.text(`Próxima visita: ${formatDate(record.next_visit_date)}`, 20, y);
        if (record.next_visit_reason) {
          doc.setFont('helvetica', 'normal');
          doc.text(` - ${record.next_visit_reason}`, 80, y);
        }
        doc.setTextColor(...COLORS.text);
        y += 5;
      }

      y += 10;
    });
  }

  // Actualizar footers con total de páginas
  const totalPages = pageNumber;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, i, totalPages);
  }

  // Descargar
  const fileName = `historia-clinica-${patient.name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

// ============================================================
// PRESUPUESTO
// ============================================================

export const generateBudgetPdf = async (budget: Budget, tenant: Tenant): Promise<void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  let y = await addHeader(doc, tenant, 'PRESUPUESTO');

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.text);

  // Recuadro con info
  doc.setFillColor(...COLORS.background);
  doc.roundedRect(15, y, pageWidth - 30, 35, 3, 3, 'F');
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.text('N° Presupuesto:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text(budget.budget_number || `#${budget.id}`, 60, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Fecha:', pageWidth / 2 + 10, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(budget.created_at), pageWidth / 2 + 30, y);
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.text('Cliente:', 20, y);
  doc.setFont('helvetica', 'normal');
  doc.text((budget.client_name || budget.owner_name || 'Consumidor Final').substring(0, 40), 60, y);

  if (budget.valid_until) {
    doc.setFont('helvetica', 'bold');
    doc.text('Válido hasta:', pageWidth / 2 + 10, y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(budget.valid_until), pageWidth / 2 + 42, y);
  }
  y += 7;

  if (budget.client_phone) {
    doc.setFont('helvetica', 'bold');
    doc.text('Teléfono:', 20, y);
    doc.setFont('helvetica', 'normal');
    doc.text(budget.client_phone, 60, y);
    y += 7;
  }
  y += 8;

  // Tabla de items
  doc.setFillColor(...COLORS.primary);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Descripción', 20, y + 5.5);
  doc.text('Cant.', pageWidth - 85, y + 5.5, { align: 'center' });
  doc.text('P. Unit.', pageWidth - 60, y + 5.5, { align: 'right' });
  doc.text('Subtotal', pageWidth - 20, y + 5.5, { align: 'right' });
  y += 12;

  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'normal');

  (budget.items || []).forEach((item: BudgetItem, index: number) => {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    if (index % 2 === 0) {
      doc.setFillColor(...COLORS.background);
      doc.rect(15, y - 4, pageWidth - 30, 8, 'F');
    }
    const line = item.subtotal != null ? item.subtotal : (item.quantity * item.unit_price);
    doc.text((item.description || '').substring(0, 45), 20, y);
    doc.text(String(item.quantity), pageWidth - 85, y, { align: 'center' });
    doc.text(formatCurrency(item.unit_price), pageWidth - 60, y, { align: 'right' });
    doc.text(formatCurrency(line), pageWidth - 20, y, { align: 'right' });
    y += 8;
  });

  // Línea separadora
  y += 2;
  doc.setDrawColor(...COLORS.border);
  doc.line(pageWidth - 80, y, pageWidth - 15, y);
  y += 8;

  // Totales
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', pageWidth - 60, y, { align: 'right' });
  doc.text(formatCurrency(budget.subtotal), pageWidth - 20, y, { align: 'right' });
  y += 6;

  if (budget.discount_amount > 0) {
    doc.setTextColor(220, 38, 38);
    doc.text('Descuento:', pageWidth - 60, y, { align: 'right' });
    doc.text(`-${formatCurrency(budget.discount_amount)}`, pageWidth - 20, y, { align: 'right' });
    doc.setTextColor(...COLORS.text);
    y += 6;
  }

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL EFECTIVO:', pageWidth - 60, y, { align: 'right' });
  doc.text(formatCurrency(budget.total), pageWidth - 20, y, { align: 'right' });
  y += 8;

  // Totales en los otros medios de pago.
  // Presupuestos viejos (sin total_card guardado) caen al recargo de tarjeta de la vete.
  const surcharge = Number(tenant.card_surcharge_percent ?? 0);
  const cardTotal =
    budget.total_card != null ? Number(budget.total_card) : budget.total * (1 + surcharge / 100);
  const installmentTotal =
    budget.total_installment != null ? Number(budget.total_installment) : null;

  doc.setFontSize(10);
  if (Math.round(cardTotal) !== Math.round(budget.total)) {
    doc.setFont('helvetica', 'normal');
    doc.text('Tarjeta / transferencia:', pageWidth - 60, y, { align: 'right' });
    doc.text(formatCurrency(cardTotal), pageWidth - 20, y, { align: 'right' });
    y += 6;
  }
  if (installmentTotal != null && Math.round(installmentTotal) !== Math.round(cardTotal)) {
    doc.setFont('helvetica', 'normal');
    doc.text('Crédito en cuotas:', pageWidth - 60, y, { align: 'right' });
    doc.text(formatCurrency(installmentTotal), pageWidth - 20, y, { align: 'right' });
    y += 6;
  }
  y += 8;

  // Validez
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.lightText);
  const validityMsg = budget.valid_until
    ? `Presupuesto válido hasta el ${formatDate(budget.valid_until)}.`
    : 'Presupuesto sin fecha de vencimiento.';
  doc.text(validityMsg, 20, y);
  y += 10;

  // Notas adicionales (al final)
  if (budget.notes) {
    if (y > 260) {
      doc.addPage();
      y = 20;
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.text);
    doc.text('Notas adicionales:', 20, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.lightText);
    const notesLines = doc.splitTextToSize(budget.notes, pageWidth - 40);
    doc.text(notesLines, 20, y);
    y += notesLines.length * 5 + 4;
  }

  addFooter(doc, 1, 1);

  doc.save(`presupuesto-${budget.budget_number || budget.id}.pdf`);
};
